import { Check, Pencil, Plus, Star, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  buildProvenancePayload,
  type ContactPointProvenanceDraft,
  DEFAULT_PROVENANCE_DRAFT,
  parseDuplicateContactPointError,
  SOURCE_TYPE_LABELS,
  toProvenanceDraft,
  validateProvenanceDraft,
} from '@/components/provider-directory/contactPointProvenance';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/catalyst/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useProviderDirectoryV2ContactPointsArchiveCreateMutation,
  useProviderDirectoryV2ContactPointsCreateMutation,
  useProviderDirectoryV2ContactPointsPartialUpdateMutation,
} from '@/services/api/generated/providerDirectoryV2ContactPointsService';
import {
  useGetProviderDirectoryLocationQuery,
  useGetProviderDirectoryServiceQuery,
} from '@/services/api/providerDirectoryV2Service';
import type {
  ProviderDirectoryV2ContactPoint,
  ProviderDirectoryV2SourceMode,
  ProviderDirectoryV2SourceType,
} from '@/services/types/provider-directory-v2-types';

const slotTitle = (slot: 'clinical' | 'billing' | 'records') => {
  if (slot === 'clinical') return 'Clinical';
  if (slot === 'billing') return 'Billing';
  return 'Records';
};

const slotPurpose = (slot: 'clinical' | 'billing' | 'records'): 'GENERAL' | 'BILLING' | 'RECORDS' => {
  if (slot === 'billing') return 'BILLING';
  if (slot === 'records') return 'RECORDS';
  return 'GENERAL';
};

const pointLabel = (p: ProviderDirectoryV2ContactPoint) => {
  if (p.point_type === 'MAILING_ADDRESS') {
    return [p.address_line1, p.city, p.state, p.postal_code]
      .filter(Boolean)
      .join(', ');
  }
  return p.value ?? '—';
};

type Slot = 'clinical' | 'billing' | 'records';
type ContactPointPurpose = 'GENERAL' | 'BILLING' | 'RECORDS';

const VERIFICATION_LABELS: Record<string, string> = {
  VERIFIED: 'Verified',
  PENDING: 'Pending',
  UNVERIFIED: 'Unverified',
  DISPUTED: 'Disputed',
  ARCHIVED: 'Archived',
};

interface EditContactsModalProps {
  open: boolean;
  onClose: () => void;
  serviceId: string;
  /** Mode: 'edit' for managing contacts, 'select' for choosing a contact */
  mode?: 'edit' | 'select';
  /** Default tab to show when modal opens */
  defaultTab?: Slot;
  /** Callback when a contact is selected (select mode only) */
  onSelectContactPoint?: (
    contactPoint: ProviderDirectoryV2ContactPoint,
    purpose: ContactPointPurpose,
  ) => void;
  /** Filter to only show these contact point types in select mode */
  allowedPointTypes?: Array<'FAX' | 'EMAIL' | 'MAILING_ADDRESS'>;
  /** Z-index for the dialog (use for nested modal contexts) */
  zIndex?: number;
}

export const EditContactsModal = ({
  open,
  onClose,
  serviceId,
  mode = 'edit',
  defaultTab = 'clinical',
  onSelectContactPoint,
  allowedPointTypes,
  zIndex,
}: EditContactsModalProps) => {
  const { data: service } = useGetProviderDirectoryServiceQuery(
    { serviceId },
    { skip: !open || !serviceId },
  );

  // NOTE: Some API responses include `*_location_id` without embedding `*_location`.
  // Be defensive so the Clinical tab doesn't end up stuck as "—" with Add disabled.
  const clinicalId =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ((service as any)?.clinical_location_id as string | undefined) ??
    service?.clinical_location?.id ??
    null;
  const billingId =
    service?.billing_location_id ?? service?.billing_location?.id ?? null;
  const recordsId =
    service?.records_location_id ?? service?.records_location?.id ?? null;

  const clinical = useGetProviderDirectoryLocationQuery(
    { locationId: clinicalId ?? '' },
    { skip: !open || !clinicalId },
  );
  const billing = useGetProviderDirectoryLocationQuery(
    { locationId: billingId ?? '' },
    { skip: !open || !billingId },
  );
  const records = useGetProviderDirectoryLocationQuery(
    { locationId: recordsId ?? '' },
    { skip: !open || !recordsId },
  );

  const [tab, setTab] = useState<Slot>(defaultTab);

  useEffect(() => {
    if (open) {
      setTab(defaultTab);
    }
  }, [defaultTab, open]);

  const [createPoint, { isLoading: isCreating }] =
    useProviderDirectoryV2ContactPointsCreateMutation();
  const [archivePoint, { isLoading: isArchiving }] =
    useProviderDirectoryV2ContactPointsArchiveCreateMutation();
  const [updatePoint, { isLoading: isUpdating }] =
    useProviderDirectoryV2ContactPointsPartialUpdateMutation();

  const slotLocationId = useMemo(() => {
    if (tab === 'clinical') return clinicalId;
    if (tab === 'billing') return billingId;
    return recordsId;
  }, [billingId, clinicalId, recordsId, tab]);

  const slotLocationName = useMemo(() => {
    if (tab === 'clinical')
      return clinical.data?.name ?? service?.clinical_location?.name ?? '—';
    if (tab === 'billing')
      return billing.data?.name ?? service?.billing_location?.name ?? '—';
    return records.data?.name ?? service?.records_location?.name ?? '—';
  }, [
    billing.data?.name,
    clinical.data?.name,
    records.data?.name,
    service?.billing_location?.name,
    service?.clinical_location?.name,
    service?.records_location?.name,
    tab,
  ]);

  const slotLocation = useMemo(() => {
    if (tab === 'clinical') return clinical.data ?? null;
    if (tab === 'billing') return billing.data ?? null;
    return records.data ?? null;
  }, [billing.data, clinical.data, records.data, tab]);

  const points = useMemo(() => {
    const all = slotLocation?.contact_points ?? [];
    const want = slotPurpose(tab);
    let filtered = all.filter((p) => (p.purpose ?? 'GENERAL') === want);

    // In select mode, filter to allowed point types only
    if (mode === 'select' && allowedPointTypes) {
      filtered = filtered.filter((p) =>
        allowedPointTypes.includes(
          p.point_type?.toUpperCase() as 'FAX' | 'EMAIL' | 'MAILING_ADDRESS',
        ),
      );
    }

    return filtered;
  }, [slotLocation?.contact_points, tab, mode, allowedPointTypes]);

  const [isAdding, setIsAdding] = useState(false);
  const [newType, setNewType] = useState<'PHONE' | 'FAX' | 'EMAIL'>('FAX');
  const [newValue, setNewValue] = useState('');
  const [newProvenance, setNewProvenance] = useState<ContactPointProvenanceDraft>(
    DEFAULT_PROVENANCE_DRAFT,
  );
  const [duplicateContactPointId, setDuplicateContactPointId] = useState<
    string | null
  >(null);
  const [duplicateMessage, setDuplicateMessage] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editName, setEditName] = useState('');
  const [editAddr1, setEditAddr1] = useState('');
  const [editAddr2, setEditAddr2] = useState('');
  const [editCity, setEditCity] = useState('');
  const [editState, setEditState] = useState('');
  const [editPostal, setEditPostal] = useState('');
  const [editFormatted, setEditFormatted] = useState('');
  const [editSourceMode, setEditSourceMode] =
    useState<ProviderDirectoryV2SourceMode>('MANUAL');
  const [editSourceType, setEditSourceType] =
    useState<ProviderDirectoryV2SourceType>('CALLED_PROVIDER');
  const [editSourceRef, setEditSourceRef] = useState('');
  const [editNote, setEditNote] = useState('');

  const handleAdd = async () => {
    if (!slotLocationId || !newValue.trim()) return;
    const provenanceError = validateProvenanceDraft(newProvenance);
    if (provenanceError) {
      toast.error(provenanceError);
      return;
    }

    setDuplicateContactPointId(null);
    setDuplicateMessage(null);

    try {
      await createPoint({
        providerDirectoryV2ContactPointCreateRequest: {
          owner_type: 'location',
          owner_id: slotLocationId,
          point_type: newType,
          purpose: slotPurpose(tab),
          value: newValue.trim(),
          is_preferred: points.length === 0,
          ...buildProvenancePayload(newProvenance),
        },
      }).unwrap();
      toast.success('Contact added');
      setNewValue('');
      setNewProvenance(DEFAULT_PROVENANCE_DRAFT);
      setIsAdding(false);
    } catch (err: unknown) {
      const duplicate = parseDuplicateContactPointError(err);
      if (duplicate) {
        setDuplicateContactPointId(duplicate.existingContactPointId);
        setDuplicateMessage(duplicate.fieldMessage);
        toast.error(duplicate.fieldMessage);
        return;
      }
      toast.error('Failed to add contact');
    }
  };

  const handleArchive = async (id: string) => {
    if (!slotLocationId) return;
    try {
      await archivePoint({
        contactPointId: id,
      }).unwrap();
      toast.success('Contact archived');
    } catch (err) {
      toast.error('Failed to archive contact');
    }
  };

  const handlePreferred = async (p: ProviderDirectoryV2ContactPoint) => {
    if (!slotLocationId) return;
    try {
      await updatePoint({
        contactPointId: p.id,
        patchedProviderDirectoryV2ContactPointPatchRequest: {
          is_preferred: true,
        },
      }).unwrap();
      toast.success('Preferred contact updated');
    } catch (err) {
      toast.error('Failed to set preferred');
    }
  };

  const startEdit = (p: ProviderDirectoryV2ContactPoint) => {
    setEditingId(p.id);
    setEditName(p.name ?? '');
    setEditValue(p.value ?? '');
    setEditAddr1(p.address_line1 ?? '');
    setEditAddr2(p.address_line2 ?? '');
    setEditCity(p.city ?? '');
    setEditState(p.state ?? '');
    setEditPostal(p.postal_code ?? '');
    setEditFormatted(p.formatted_address ?? '');
    const provenance = toProvenanceDraft(p);
    setEditSourceMode(provenance.sourceMode);
    setEditSourceType(provenance.sourceType);
    setEditSourceRef(provenance.sourceRef);
    setEditNote(provenance.note);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditValue('');
    setEditAddr1('');
    setEditAddr2('');
    setEditCity('');
    setEditState('');
    setEditPostal('');
    setEditFormatted('');
    setEditSourceMode('MANUAL');
    setEditSourceType('CALLED_PROVIDER');
    setEditSourceRef('');
    setEditNote('');
  };

  const saveEdit = async (p: ProviderDirectoryV2ContactPoint) => {
    if (!slotLocationId) return;
    const provenanceError = validateProvenanceDraft({
      sourceMode: editSourceMode,
      sourceType: editSourceType,
      sourceRef: editSourceRef,
      note: editNote,
    });
    if (provenanceError) {
      toast.error(provenanceError);
      return;
    }

    setDuplicateContactPointId(null);
    setDuplicateMessage(null);

    try {
      if (p.point_type === 'MAILING_ADDRESS') {
        await updatePoint({
          contactPointId: p.id,
          patchedProviderDirectoryV2ContactPointPatchRequest: {
            name: editName.trim() || null,
            address_line1: editAddr1.trim() || null,
            address_line2: editAddr2.trim() || null,
            city: editCity.trim() || null,
            state: editState.trim() || null,
            postal_code: editPostal.trim() || null,
            formatted_address: editFormatted.trim() || null,
            ...buildProvenancePayload({
              sourceMode: editSourceMode,
              sourceType: editSourceType,
              sourceRef: editSourceRef,
              note: editNote,
            }),
          },
        }).unwrap();
      } else {
        if (!editValue.trim()) {
          toast.error('Value is required.');
          return;
        }
        await updatePoint({
          contactPointId: p.id,
          patchedProviderDirectoryV2ContactPointPatchRequest: {
            name: editName.trim() || null,
            value: editValue.trim(),
            ...buildProvenancePayload({
              sourceMode: editSourceMode,
              sourceType: editSourceType,
              sourceRef: editSourceRef,
              note: editNote,
            }),
          },
        }).unwrap();
      }
      toast.success('Contact updated');
      cancelEdit();
    } catch (err: unknown) {
      const duplicate = parseDuplicateContactPointError(err);
      if (duplicate) {
        setDuplicateContactPointId(duplicate.existingContactPointId);
        setDuplicateMessage(duplicate.fieldMessage);
        toast.error(duplicate.fieldMessage);
        return;
      }
      toast.error('Failed to update contact');
    }
  };

  const busy = isCreating || isArchiving || isUpdating;
  const duplicatePoint = duplicateContactPointId
    ? points.find((p) => p.id === duplicateContactPointId) ?? null
    : null;

  const handleUseExistingDuplicate = async () => {
    if (!duplicatePoint) return;

    if (mode === 'select') {
      onSelectContactPoint?.(duplicatePoint, slotPurpose(tab));
      onClose();
      return;
    }

    await handlePreferred(duplicatePoint);
    setDuplicateContactPointId(null);
    setDuplicateMessage(null);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : null)}>
      <DialogContent className="max-w-3xl" zIndex={zIndex}>
        <DialogHeader>
          <DialogTitle>
            {mode === 'select' ? 'Select destination' : 'Edit contacts'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'select'
              ? 'Select a contact method to use for this request.'
              : 'Update contact methods on the routing nodes for this service.'}
          </DialogDescription>
        </DialogHeader>

        {!service ? (
          <div className="text-sm text-zinc-600">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2">
              <div className="text-xs text-zinc-500">Service</div>
              <div className="text-sm font-medium text-zinc-900">
                {service.name}
              </div>
            </div>

            <Tabs value={tab} onValueChange={(v) => setTab(v as Slot)}>
              <div className="flex items-center justify-between gap-3">
                <TabsList>
                  <TabsTrigger value="clinical">Clinical</TabsTrigger>
                  <TabsTrigger value="billing" disabled={!billingId}>
                    Billing
                  </TabsTrigger>
                  <TabsTrigger value="records" disabled={!recordsId}>
                    Records
                  </TabsTrigger>
                </TabsList>

                {mode !== 'select' && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsAdding((v) => !v)}
                      disabled={!slotLocationId}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add contact
                    </Button>
                  </div>
                )}
              </div>

              <TabsContent value="clinical" className="mt-3">
                <div className="text-xs text-zinc-500">{slotLocationName}</div>
              </TabsContent>
              <TabsContent value="billing" className="mt-3">
                <div className="text-xs text-zinc-500">{slotLocationName}</div>
              </TabsContent>
              <TabsContent value="records" className="mt-3">
                <div className="text-xs text-zinc-500">{slotLocationName}</div>
              </TabsContent>
            </Tabs>

            {!slotLocationId ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                This service doesn’t have a {slotTitle(tab).toLowerCase()}{' '}
                routing location yet. Use{' '}
                <span className="font-semibold">Edit routing</span> to set one,
                then add contacts here.
              </div>
            ) : null}

            {isAdding ? (
              <div className="rounded-xl border border-zinc-200 bg-white p-4">
                <div className="text-sm font-semibold text-zinc-900">
                  Add {slotTitle(tab)} contact
                </div>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
                  <select
                    className="h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                    value={newType}
                    onChange={(e) =>
                      setNewType(e.target.value as 'PHONE' | 'FAX' | 'EMAIL')
                    }
                  >
                    <option value="FAX">Fax</option>
                    <option value="PHONE">Phone</option>
                    <option value="EMAIL">Email</option>
                  </select>
                  <Input
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    placeholder="Value…"
                    className="md:col-span-2"
                  />
                </div>

                <div className="mt-3 rounded-lg border border-zinc-200 bg-zinc-50 p-3 space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-wide text-zinc-600">
                    Source Attribution
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <select
                      className="h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                      value={newProvenance.sourceMode}
                      onChange={(e) =>
                        setNewProvenance((prev) => ({
                          ...prev,
                          sourceMode: e.target.value as ProviderDirectoryV2SourceMode,
                        }))
                      }
                    >
                      <option value="MANUAL">Manual</option>
                      <option value="SYSTEM">System</option>
                    </select>
                    <select
                      className="h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                      value={newProvenance.sourceType}
                      onChange={(e) =>
                        setNewProvenance((prev) => ({
                          ...prev,
                          sourceType: e.target.value as ProviderDirectoryV2SourceType,
                        }))
                      }
                      disabled={newProvenance.sourceMode !== 'MANUAL'}
                    >
                      {Object.entries(SOURCE_TYPE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {newProvenance.sourceMode === 'MANUAL' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <Input
                        value={newProvenance.sourceRef}
                        onChange={(e) =>
                          setNewProvenance((prev) => ({
                            ...prev,
                            sourceRef: e.target.value,
                          }))
                        }
                        placeholder="Source reference (URL or call note)"
                      />
                      <Input
                        value={newProvenance.note}
                        onChange={(e) =>
                          setNewProvenance((prev) => ({
                            ...prev,
                            note: e.target.value,
                          }))
                        }
                        placeholder="Note"
                      />
                    </div>
                  )}
                </div>

                <div className="mt-3 flex items-center justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsAdding(false);
                      setNewValue('');
                      setNewProvenance(DEFAULT_PROVENANCE_DRAFT);
                    }}
                    disabled={busy}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAdd}
                    disabled={!newValue.trim() || busy}
                  >
                    Save
                  </Button>
                </div>
              </div>
            ) : null}

            {duplicatePoint && duplicateMessage && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <div>{duplicateMessage}</div>
                <div className="mt-2 flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleUseExistingDuplicate}
                  >
                    Use existing
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setDuplicateContactPointId(null);
                      setDuplicateMessage(null);
                    }}
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            )}

            <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
                <div className="text-sm font-semibold text-zinc-900">
                  {slotTitle(tab)} contacts
                </div>
                <div className="text-xs text-zinc-500 tabular-nums">
                  {points.length}
                </div>
              </div>

              <div className="divide-y divide-zinc-100">
                {points.map((p) => (
                  <div key={p.id} className="px-4 py-3 flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="text-xs font-semibold text-zinc-600">
                          {p.point_type}
                        </div>
                        {p.is_preferred ? (
                          <span className="text-[11px] font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5">
                            Preferred
                          </span>
                        ) : null}
                        {p.source_mode === 'MANUAL' && (
                          <span className="text-[11px] font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded px-1.5 py-0.5">
                            Manual
                          </span>
                        )}
                        {p.source_mode === 'MANUAL' && p.source_type && (
                          <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                            Source-attributed
                          </span>
                        )}
                        <span className="text-[11px] font-medium text-zinc-700 bg-zinc-100 border border-zinc-200 rounded px-1.5 py-0.5">
                          {VERIFICATION_LABELS[p.verification_status ?? 'UNVERIFIED'] ??
                            'Unverified'}
                        </span>
                      </div>

                      {editingId === p.id ? (
                        <div className="mt-2 space-y-2">
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            placeholder="Label (optional)…"
                          />
                          {p.point_type === 'MAILING_ADDRESS' ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              <Input
                                value={editAddr1}
                                onChange={(e) => setEditAddr1(e.target.value)}
                                placeholder="Address line 1…"
                              />
                              <Input
                                value={editAddr2}
                                onChange={(e) => setEditAddr2(e.target.value)}
                                placeholder="Address line 2…"
                              />
                              <Input
                                value={editCity}
                                onChange={(e) => setEditCity(e.target.value)}
                                placeholder="City…"
                              />
                              <Input
                                value={editState}
                                onChange={(e) => setEditState(e.target.value)}
                                placeholder="State…"
                              />
                              <Input
                                value={editPostal}
                                onChange={(e) => setEditPostal(e.target.value)}
                                placeholder="Postal code…"
                              />
                              <Input
                                value={editFormatted}
                                onChange={(e) =>
                                  setEditFormatted(e.target.value)
                                }
                                placeholder="Formatted (optional)…"
                              />
                            </div>
                          ) : (
                            <Input
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              placeholder="Value…"
                            />
                          )}

                          <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 space-y-2">
                            <div className="text-xs font-semibold uppercase tracking-wide text-zinc-600">
                              Source Attribution
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              <select
                                className="h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                                value={editSourceMode}
                                onChange={(e) =>
                                  setEditSourceMode(
                                    e.target.value as ProviderDirectoryV2SourceMode,
                                  )
                                }
                              >
                                <option value="MANUAL">Manual</option>
                                <option value="SYSTEM">System</option>
                              </select>
                              <select
                                className="h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                                value={editSourceType}
                                onChange={(e) =>
                                  setEditSourceType(
                                    e.target.value as ProviderDirectoryV2SourceType,
                                  )
                                }
                                disabled={editSourceMode !== 'MANUAL'}
                              >
                                {Object.entries(SOURCE_TYPE_LABELS).map(
                                  ([value, label]) => (
                                    <option key={value} value={value}>
                                      {label}
                                    </option>
                                  ),
                                )}
                              </select>
                            </div>
                            {editSourceMode === 'MANUAL' && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <Input
                                  value={editSourceRef}
                                  onChange={(e) =>
                                    setEditSourceRef(e.target.value)
                                  }
                                  placeholder="Source reference (URL or call note)"
                                />
                                <Input
                                  value={editNote}
                                  onChange={(e) => setEditNote(e.target.value)}
                                  placeholder="Note"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="text-sm text-zinc-900 mt-1 break-all">
                            {pointLabel(p)}
                          </div>
                          {p.name ? (
                            <div className="text-xs text-zinc-500 mt-1">
                              {p.name}
                            </div>
                          ) : null}
                          {p.source_mode === 'MANUAL' && (
                            <div className="text-xs text-zinc-500 mt-1">
                              Source:{' '}
                              {p.source_type
                                ? SOURCE_TYPE_LABELS[p.source_type] ??
                                  p.source_type
                                : 'Unspecified'}
                              {p.source_ref ? ` · ${p.source_ref}` : ''}
                              {p.note ? ` · ${p.note}` : ''}
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {/* Selection mode: "Use this contact" button */}
                      {mode === 'select' && (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => {
                            onSelectContactPoint?.(p, slotPurpose(tab));
                            onClose();
                          }}
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Use this contact
                        </Button>
                      )}

                      {/* Edit mode buttons */}
                      {mode !== 'select' &&
                        (editingId === p.id ? (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => saveEdit(p)}
                              disabled={busy}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Save
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={cancelEdit}
                              disabled={busy}
                            >
                              <X className="h-4 w-4 text-zinc-500" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePreferred(p)}
                              disabled={busy || Boolean(p.is_preferred)}
                            >
                              <Star className="h-4 w-4 mr-1" />
                              Prefer
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => startEdit(p)}
                              disabled={busy}
                            >
                              <Pencil className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleArchive(p.id)}
                              disabled={busy}
                            >
                              <Trash2 className="h-4 w-4 text-zinc-500" />
                            </Button>
                          </>
                        ))}
                    </div>
                  </div>
                ))}

                {points.length === 0 ? (
                  <div className="px-4 py-8 text-sm text-zinc-600">
                    {mode === 'select'
                      ? 'No selectable contacts available for this location.'
                      : 'No contacts yet. Add one to get started.'}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
